// @ts-nocheck
import axios from "axios";
// Only import Sentry in non-development environments
let Sentry = null;
if (process.env.VUE_APP_ENVIRONMENT !== "development") {
  Sentry = require("@sentry/vue");
}

import { CONFIG } from "@/const/config";
import {
  getAccessToken,
  getBrowserName,
  getPlatform,
  getRegistrationToken,
  isAuth,
  logout,
  setAccessToken,
  setAuthRefreshToken,
  setAuthRefreshTokenExpirationDate
} from "@/helpers/utils";
import { getCookie, callVueError, getVueStore, getVueRouter } from "@/helpers/vueInstance";

/**
 * Send the user back to /app/login on session loss.
 * Prefers a Vue Router push (preserves SPA state, avoids full reload) and falls
 * back to a hard navigation only if the router instance isn't ready yet.
 * Adds ?reason=session-expired so the login screen can show a banner.
 */
async function redirectToLoginExpired() {
  logout();
  const router = getVueRouter();
  const target = { path: '/app/login', query: { reason: 'session-expired' } };
  if (router) {
    try {
      await router.push(target);
      return;
    } catch (e) {
      // navigation might be aborted (e.g. duplicate); fall through to hard redirect
      console.warn('[Interceptors] router.push failed, falling back to hard redirect', e);
    }
  }
  window.location.href = '/app/login?reason=session-expired';
}

const useRefreshTokenInterceptor = async () => {
  const userId = getCookie("auth_user_id");
  const refreshToken = getCookie("auth_refresh_token");
  const clientId = getCookie("entityId");
  const accessToken = await getAccessToken();
  if (userId && refreshToken && clientId && accessToken) {
    try {
      const { status, data } = await instance.post("/api/auth/current-company-refresh", {
        userId,
        refreshToken,
        clientId,
      }, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (status === 200) {
        await setAccessToken(data.accessToken);
        await setAuthRefreshToken(data.refreshToken ,data?.refreshTokenExpiredTo);
        await setAuthRefreshTokenExpirationDate(data?.refreshTokenExpiredTo)
        return { status, data };
      }
    } catch (e) {
      await redirectToLoginExpired();
      throw e;
    }
  }
  await redirectToLoginExpired();
  throw new Error("Something went wrong");
};

// Determine baseURL from environment or use default
function getInitialBaseURL(): string {
  // Use environment variable if available, otherwise fall back to dev API
  const baseURL = CONFIG.VUE_APP_API_URL || ''

  console.log('[Interceptors] Axios baseURL:', baseURL)

  return baseURL
}

export const instance = axios.create({
  baseURL: getInitialBaseURL(),
  headers: {
    "x-api-key": CONFIG.VUE_APP_X_API_KEY,
    "x-version": `${getBrowserName(navigator.userAgent)} , ${getPlatform(navigator.appVersion)}, build date: ${CONFIG.VUE_APP_BUILD_DATE}`,
  },
});

instance.interceptors.request.use(async (config) => {
  // Ensure baseURL is always set (fallback if somehow cleared)
  if (!config.baseURL) {
    config.baseURL = instance.defaults.baseURL || CONFIG.VUE_APP_API_URL;
  }

  if (config.headers.Authorization && config.headers?.isUniqueToken) {
    return config;
  }
  const accessToken = await getAccessToken();
  if (config.headers && accessToken && !isAuth) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  const registrationToken = await getRegistrationToken();
  if (config.headers && isAuth && registrationToken) {
    config.headers.Authorization = `Bearer ${registrationToken}`;
  }

  return config;
});

class checkTokenIsRefreshed {
  static _tokenIsRefreshed = false;

  static instance = null;
}

async function tokenIsRefreshed(originalRequest) {
  if (checkTokenIsRefreshed._tokenIsRefreshed && getAccessToken()) {
    checkTokenIsRefreshed._tokenIsRefreshed = false;
    originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
    return instance.request(originalRequest);
  }
  let timer;
  return (() => {
    clearTimeout(timer);
    timer = setTimeout(() => tokenIsRefreshed(originalRequest), 1000);
  })();
}

instance.interceptors.response.use(
  (config) => config,
  async (error) => {
    console.log(error, "error");

    // Safety check: ensure error is valid
    if (!error) {
      console.error('Invalid error object received in interceptor');
      throw new Error('Invalid error object');
    }

    const xTraceId = error?.response?.headers['custom-x-trace-id']

    if(xTraceId && xTraceId.length) {
      const store = getVueStore();
      if (store) {
        store.commit("SET_X_TRACE_ID", xTraceId);
      }
      // Only capture Sentry exceptions in non-development environments
      if (process.env.VUE_APP_ENVIRONMENT !== "development" && Sentry) {
        Sentry.captureException(new Error(xTraceId), (scope) => {
          scope.clear();
          scope.setTag("x_trace_id", xTraceId);
          return scope;
        });
      }
    }

    const originalRequest = error.config;
    if (error.response && error.response.status === 401 && originalRequest && !originalRequest._retry) {
      if (getAccessToken()) {
        if (!checkTokenIsRefreshed.instance) {
          checkTokenIsRefreshed.instance = originalRequest;
          try {
            const { status } = await useRefreshTokenInterceptor();
            if (status === 200) {
              checkTokenIsRefreshed.instance = null;
              checkTokenIsRefreshed._tokenIsRefreshed = true;
              return instance.request(originalRequest);
            }
          } catch (e) {
            console.log(e);
          }
        } else {
          await tokenIsRefreshed(originalRequest);
        }
      } else {
        if (getRegistrationToken()) {
          originalRequest.headers.Authorization = `Bearer ${getRegistrationToken()}`;
          return instance.request(originalRequest);
        }

        const store = getVueStore();
				if (store && !store.getters?.has_expired_mode) {
					store.commit("SET_HAS_EXPIRED_MODE", true);
					await callVueError("Your session has expired", "", false, true);
				}

        // throw new Error("Something went wrong");
      }

      // if (getRefreshToken() && getAccessToken() && checkTokenIsRefreshed._tokenIsRefreshed) {
      //   return instance.request(originalRequest);
      // } else {
      //   const status = originalRequest
      // }
      // return instance.request(originalRequest);
    }
    if (error.response && error.response.status === 400) {
      if(originalRequest.url.includes("/api/registration/personal-info-shareholders")) return
      if(originalRequest.url.includes("/api/payments/attachOrderFile") && error.response.data?.code === 'AWSUploadFailed') {
        return
      }
      if(originalRequest.url.includes("/api/payments/client/")) {
        if(error.response.data?.errors[0]?.message === 'ClientNotFound') {
          throw error;
        }
      }
      if(originalRequest.url.includes("/api/payments/bank-details/")) {
        throw error;
      }
      if(originalRequest.url.includes("/accept")) {
        throw error;
      }
      if(originalRequest.url.includes("/api/users")) {
        throw error;
      }
    }
    // if (error.response?.status === 404) {
    //   if(originalRequest.url.includes("/api/auth/current-company-refresh")) {
    //     await instance?.appContext.config.globalProperties.$error("", "login");
    //     throw error;
    //   }
    // }
    // Suppress error modals during new company wizard (window.__wizardActive flag)
    const suppressModal = window.__wizardActive === true;
    if (!suppressModal && error.response && error.response.status !== 422 && error.response.status !== 401 && !originalRequest.url.includes("/api/auth/verify-otp") && !originalRequest.url.includes("/api/auth/init-otp") && !originalRequest.url.includes("/api/registration/") && !originalRequest.url.includes("/api/payments/validate-iban") && !originalRequest.url.includes("/api/registration/shareholder-company/getByShareHolder") && !originalRequest.url.includes("/api/cards/complete-activate") && !originalRequest.url.includes("/api/auth/current-company-refresh") && !originalRequest.url.includes("/api/crypto/address") && !originalRequest.url.includes("/api/savings")) {
      console.log(error);
      if (error.response.data?.message) {
        await callVueError(error.response.data.message);
        throw error;
      }
      if(error?.response?.data?.providerErrors?.length > 0) {
        const formatpProviderErrors = error?.response?.data?.providerErrors.map(el => {
          return `<div class="body2 black_white--text">${el.message}</div>`
        })
        await callVueError(formatpProviderErrors.join('<br>'), null, true);
        throw error;
      }
      if (error.response.data?.errors && error.response.data?.errors[0]?.message) {
        await callVueError(error.response.data?.errors[0]?.message);
        throw error;
      }
      await callVueError(error.message || "Something went wrong!");
      throw error;
    }
    throw error;
  },
);

export default instance;
